const API_KEY = process.env.REACT_APP_POLYGON_API_KEY;
const WS_URL = 'wss://socket.polygon.io/stocks';
const AGGREGATION_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_INTERVAL = 30000; // 30 seconds heartbeat to keep connection alive
const SUBSCRIPTION_BATCH_SIZE = 500; // Max symbols per subscription message
const SUBSCRIPTION_BATCH_DELAY = 100; // Delay between subscription batches

class StockWebSocket {
  constructor(onPriceUpdate, onConnectionChange = null, onFallbackNeeded = null) {
    this.ws = null;
    this.onPriceUpdate = onPriceUpdate;
    this.onConnectionChange = onConnectionChange;
    this.onFallbackNeeded = onFallbackNeeded; // Called when max reconnect attempts reached
    this.subscribedSymbols = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnected = false;
    this.marketOpen = false; // Track market status for reconnection logic
    this.intentionalDisconnect = false; // Flag to prevent reconnect on intentional close

    // Heartbeat timer
    this.heartbeatTimer = null;

    // 30-second aggregation buffers
    this.aggregateBuffers = {}; // { symbol: { data: [], high, low, startTime } }
    this.lastKnownPrices = {}; // Store last known price for each symbol

    // Web Worker for accurate timing (not affected by background throttling)
    this.worker = null;
    this.initWorker();
  }

  // Initialize Web Worker for aggregation timer
  initWorker() {
    try {
      this.worker = new Worker(`${process.env.PUBLIC_URL}/aggregationWorker.js`);

      this.worker.onmessage = (e) => {
        if (e.data.type === 'flush') {
          this.flushBuffers();
        }
      };

      this.worker.onerror = (error) => {
        console.error('Worker error:', error);
        // Fallback to setInterval if worker fails
        this.useFallbackTimer();
      };

      console.log('Web Worker initialized for aggregation timer');
    } catch (error) {
      console.warn('Web Worker not supported, using fallback timer:', error);
      this.useFallbackTimer();
    }
  }

  // Fallback to setInterval if Web Worker is not available
  useFallbackTimer() {
    this.worker = null;
    this.fallbackTimer = null;
  }

  // Start the aggregation timer (via Worker or fallback)
  startAggregationTimer() {
    if (this.worker) {
      console.log('Starting aggregation timer via Web Worker');
      this.worker.postMessage({ action: 'start' });
    } else {
      // Fallback to setInterval
      console.log('Starting aggregation timer via setInterval (fallback)');
      if (this.fallbackTimer) {
        clearInterval(this.fallbackTimer);
      }
      this.fallbackTimer = setInterval(() => {
        this.flushBuffers();
      }, AGGREGATION_INTERVAL);
    }
  }

  // Stop the aggregation timer
  stopAggregationTimer() {
    if (this.worker) {
      this.worker.postMessage({ action: 'stop' });
    }
    if (this.fallbackTimer) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = null;
    }
  }

  // Start heartbeat to keep connection alive
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Polygon.io doesn't require explicit ping, but we can send a harmless message
        // to keep the connection active. Actually, the WebSocket protocol handles ping/pong
        // automatically, but we'll log to track connection health.
        console.log('Heartbeat: WebSocket connection active');
      }
    }, HEARTBEAT_INTERVAL);
  }

  // Stop heartbeat
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  connect() {
    return new Promise((resolve, reject) => {
      try {
        this.intentionalDisconnect = false;
        this.ws = new WebSocket(WS_URL);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.authenticate();
        };

        this.ws.onmessage = (event) => {
          const messages = JSON.parse(event.data);
          this.handleMessages(messages, resolve);
        };

        this.ws.onerror = (event) => {
          console.error('WebSocket error:', event);
          // Convert Event to Error for proper error handling
          const error = new Error('WebSocket connection failed');
          error.event = event;
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log(`WebSocket disconnected - code: ${event.code}, reason: "${event.reason || 'none'}", wasClean: ${event.wasClean}`);
          console.log(`intentionalDisconnect: ${this.intentionalDisconnect}`);
          this.setConnected(false);
          this.stopAggregationTimer();
          this.stopHeartbeat();

          // Only attempt reconnect if not intentionally disconnected
          if (!this.intentionalDisconnect) {
            console.log('Triggering reconnect...');
            this.attemptReconnect();
          } else {
            console.log('Skipping reconnect (intentional disconnect)');
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  authenticate() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        action: 'auth',
        params: API_KEY
      }));
    }
  }

  handleMessages(messages, resolveConnect) {
    if (!Array.isArray(messages)) {
      messages = [messages];
    }

    messages.forEach(msg => {
      switch (msg.ev) {
        case 'status':
          if (msg.status === 'connected') {
            console.log('WebSocket status: connected');
          } else if (msg.status === 'auth_success') {
            console.log('WebSocket authenticated');
            this.setConnected(true);
            this.reconnectAttempts = 0;
            this.startHeartbeat(); // Start heartbeat after authentication
            if (resolveConnect) resolveConnect();
            // Re-subscribe if reconnecting
            if (this.subscribedSymbols.length > 0) {
              this.subscribe(this.subscribedSymbols);
            }
          } else if (msg.status === 'auth_failed') {
            console.error('WebSocket authentication failed');
          } else if (msg.status === 'max_connections') {
            console.error('WebSocket max connections reached');
          }
          // Skip logging individual subscription confirmations (too verbose)
          // else { console.log('WebSocket status:', msg.status, msg.message || ''); }
          break;

        case 'A':
          // Aggregate Second data - collect for 30-second aggregation
          this.addToBuffer(msg);
          break;

        case 'AM':
          // Aggregate Minute data (fallback, shouldn't receive if subscribed to A.*)
          if (this.onPriceUpdate) {
            this.onPriceUpdate({
              symbol: msg.sym,
              price: msg.c,
              open: msg.o,
              high: msg.h,
              low: msg.l,
              volume: msg.v,
              vwap: msg.vw,
              timestamp: msg.e
            });
          }
          break;

        default:
          // Other message types
          break;
      }
    });
  }

  // Add per-second data to 30-second buffer
  addToBuffer(msg) {
    const symbol = msg.sym;
    const price = msg.c;  // close price
    const volume = msg.v || 0;
    const timestamp = msg.e || Date.now();

    if (!this.aggregateBuffers[symbol]) {
      this.aggregateBuffers[symbol] = {
        data: [],  // Store { price, volume, timestamp } for sorting
        high: -Infinity,
        low: Infinity,
        startTime: timestamp
      };
    }

    const buffer = this.aggregateBuffers[symbol];

    // Store data with timestamp for potential sorting
    buffer.data.push({ price, volume, timestamp });

    // Update high/low
    buffer.high = Math.max(buffer.high, price);
    buffer.low = Math.min(buffer.low, price);

    // Update last known price
    this.lastKnownPrices[symbol] = price;
  }

  // Flush all buffers and emit 30-second aggregated data
  flushBuffers() {
    const now = Date.now();

    // Track symbols that received fresh data (to avoid duplicate emit)
    const symbolsWithData = new Set();

    // Process buffers for symbols that received data
    Object.keys(this.aggregateBuffers).forEach(symbol => {
      const buffer = this.aggregateBuffers[symbol];

      if (buffer.data.length > 0) {
        // Mark this symbol as having fresh data
        symbolsWithData.add(symbol);

        // Sort by timestamp to handle out-of-order data
        buffer.data.sort((a, b) => a.timestamp - b.timestamp);

        const firstData = buffer.data[0];
        const lastData = buffer.data[buffer.data.length - 1];
        const totalVolume = buffer.data.reduce((sum, d) => sum + d.volume, 0);

        const aggregatedData = {
          symbol,
          price: lastData.price,      // close = last price
          open: firstData.price,       // open = first price
          high: buffer.high,
          low: buffer.low,
          volume: totalVolume,
          timestamp: now
        };

        // Update last known price
        this.lastKnownPrices[symbol] = lastData.price;

        if (this.onPriceUpdate) {
          this.onPriceUpdate(aggregatedData);
        }
      }

      // Reset buffer
      this.aggregateBuffers[symbol] = {
        data: [],
        high: -Infinity,
        low: Infinity,
        startTime: now
      };
    });

    // For symbols with no data in this interval, emit last known price
    // This keeps the chart updated even during low-activity periods
    // Skip symbols that already emitted fresh data above
    this.subscribedSymbols.forEach(symbol => {
      if (!symbolsWithData.has(symbol)) {
        const lastPrice = this.lastKnownPrices[symbol];
        if (lastPrice && this.onPriceUpdate) {
          this.onPriceUpdate({
            symbol,
            price: lastPrice,
            open: lastPrice,
            high: lastPrice,
            low: lastPrice,
            volume: 0,
            timestamp: now,
            isStale: true  // Flag to indicate this is repeated data
          });
        }
      }
    });
  }

  subscribe(symbols) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not ready, queuing subscription');
      this.subscribedSymbols = symbols;
      return;
    }

    // Store for reconnection
    this.subscribedSymbols = symbols;

    console.log(`Subscribing to ${symbols.length} symbols (per-second data, 30s aggregation)`);

    // Batch subscriptions to avoid overwhelming the server
    this.subscribeInBatches(symbols);

    // Start the aggregation timer
    this.startAggregationTimer();
  }

  // Subscribe in batches to avoid message size limits
  async subscribeInBatches(symbols) {
    const totalBatches = Math.ceil(symbols.length / SUBSCRIPTION_BATCH_SIZE);

    for (let i = 0; i < symbols.length; i += SUBSCRIPTION_BATCH_SIZE) {
      const batch = symbols.slice(i, i + SUBSCRIPTION_BATCH_SIZE);
      const batchNum = Math.floor(i / SUBSCRIPTION_BATCH_SIZE) + 1;
      const params = batch.map(s => `A.${s}`).join(',');

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log(`Subscribing batch ${batchNum}/${totalBatches} (${batch.length} symbols)`);
        this.ws.send(JSON.stringify({
          action: 'subscribe',
          params: params
        }));

        // Small delay between batches to avoid rate limiting
        if (i + SUBSCRIPTION_BATCH_SIZE < symbols.length) {
          await new Promise(resolve => setTimeout(resolve, SUBSCRIPTION_BATCH_DELAY));
        }
      } else {
        console.warn('WebSocket closed during batch subscription');
        break;
      }
    }
  }

  unsubscribe(symbols) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    const params = symbols.map(s => `A.${s}`).join(',');

    this.ws.send(JSON.stringify({
      action: 'unsubscribe',
      params: params
    }));

    this.subscribedSymbols = this.subscribedSymbols.filter(
      s => !symbols.includes(s)
    );

    // Stop timer if no more subscriptions
    if (this.subscribedSymbols.length === 0) {
      this.stopAggregationTimer();
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached, triggering fallback');
      if (this.onFallbackNeeded) {
        this.onFallbackNeeded(true); // Signal to use REST fallback
      }
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting reconnect ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    setTimeout(() => {
      this.connect().catch(err => {
        console.error('Reconnect failed:', err.message);
        // Will trigger another reconnect attempt via onclose handler
      });
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  disconnect() {
    this.intentionalDisconnect = true; // Prevent reconnection attempts
    this.stopAggregationTimer();
    this.stopHeartbeat();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.setConnected(false);
    }
  }

  // Helper to set connection status and notify callback
  setConnected(status) {
    if (this.isConnected !== status) {
      this.isConnected = status;
      if (this.onConnectionChange) {
        this.onConnectionChange(status);
      }
      // When reconnected, signal to stop REST fallback
      if (status && this.onFallbackNeeded) {
        this.onFallbackNeeded(false);
      }
    }
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  // Update market status - called from parent component
  // When market becomes active, reset reconnect attempts and try to connect
  setMarketOpen(isActive) {
    const wasActive = this.marketOpen;
    this.marketOpen = isActive;

    // If market just became active and we're disconnected, try to connect
    if (isActive && !wasActive && !this.isConnected && this.subscribedSymbols.length > 0) {
      console.log('Market became active, initiating WebSocket connection');
      this.reconnectAttempts = 0;
      this.connect().then(() => {
        this.subscribe(this.subscribedSymbols);
      }).catch(err => {
        console.error('Failed to connect on market open:', err);
      });
    }
  }
}

export default StockWebSocket;
